import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { trackPageView, grantAnalyticsConsent } from './utils/analytics';
import { getStoredTheme, toggleTheme } from './utils/theme';
import { VOIVODESHIPS, formatVoivodeshipLabel, getVoivodeshipForEvent, Voivodeship } from './data/cityVoivodeships';
import { FeaturesProvider, useFeatures } from './hooks/useFeatures';
import { useStandalone } from './hooks/useStandalone';
import apiService from './services/api';
import EventForm from './components/EventForm';
import Admin from './components/Admin';
import About from './components/About';
import PrivacyPolicy from './components/PrivacyPolicy';
import DozynkiMap from './components/DozynkiMap';
import EventsMap from './components/EventsMap';
import InstallPrompt from './components/InstallPrompt';
import { OfflineBanner } from './components/OfflineBanner';
import NewsBanner from './components/NewsBanner';
import './App.css';

interface Event {
  id: number | string;
  name: string;
  city: string;
  voivodeship?: string;
  date: string;
  time?: string;
  type: string;
  price?: string;
  showPrice?: boolean;
  address?: string;
  link?: string;
  description?: string;
  multiDay?: boolean;
  isMultiDay?: boolean;
  endDate?: string;
  organizer?: string;
  phone?: string;
  email?: string;
  coordinates?: [number, number];
  createdAt?: string;
  updatedAt?: string;
  originalEventId?: number;
}

// Funkcja do pobierania emotki dla typu eventu
const getTypeEmoji = (type: string) => {
  const typeEmojis: { [key: string]: string } = {
    'Koncert': '🎵',
    'Kultura': '🎭',
    'Jedzenie': '🍽️',
    'Festiwal': '🎪',
    'Bary': '🍻',
    'Sport': '⚽',
    'Nauka': '🔬',
    'Kino': '🎬',
    'Targi': '🏪',
    'Kluby': '🎤'
  };
  return typeEmojis[type] || '🎯';
};

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    grantAnalyticsConsent();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  return (
    <FeaturesProvider>
      <Router>
        <PageViewTracker />
        <OfflineBanner />
        <Routes>
          <Route path="/k9Jx4LwzTqB1VmN" element={<Admin />} />
          <Route path="/about" element={<About />} />
          <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
          <Route path="/dozynki" element={<DozynkiRoute />} />
          <Route path="/mapa" element={<EventsMap />} />
          <Route path="/" element={<MainPage />} />
        </Routes>
      </Router>
    </FeaturesProvider>
  );
}

function DozynkiRoute() {
  const { dozynki, ready } = useFeatures();
  if (!ready) return null;
  if (!dozynki) return <Navigate to="/" replace />;
  return <DozynkiMap />;
}

function MainPage() {
  const { dozynki, ready } = useFeatures();
  const isStandalone = useStandalone();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [filters, setFilters] = useState<{
    voivodeship: Voivodeship | '';
    type: string;
    date: string;
  }>({
    voivodeship: '',
    type: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => getStoredTheme() === 'dark');

  // Ładowanie eventów z API
  useEffect(() => {
    const loadEvents = async () => {
      setIsLoading(true);
      try {
        const eventsFromAPI = await apiService.getEvents();
        setEvents(eventsFromAPI);
        setFilteredEvents(eventsFromAPI);
      } catch (error) {
        console.error('Error loading events:', error);
        setEvents([]);
        setFilteredEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  // Funkcja do generowania wydarzeń wielodniowych dla użytkowników
  const generateMultiDayEvents = (events: Event[]) => {
    const expandedEvents: Event[] = [];
    
    events.forEach(event => {
      if (event.isMultiDay && event.endDate) {
        // Wydarzenie wielodniowe - generuj wydarzenie dla każdego dnia
        const startDate = new Date(event.date);
        const endDate = new Date(event.endDate);
        
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateStr = d.toISOString().split('T')[0];
          expandedEvents.push({
            ...event,
            id: `${event.id}_${dateStr}`, // Unikalne ID dla każdego dnia
            date: dateStr,
            isMultiDay: false, // Dla użytkownika to jest wydarzenie jednodniowe
            originalEventId: typeof event.id === 'number' ? event.id : parseInt(event.id.toString()) // Zachowaj ID oryginalnego wydarzenia
          });
        }
      } else {
        // Wydarzenie jednodniowe - dodaj bez zmian
        expandedEvents.push(event);
      }
    });
    
    return expandedEvents;
  };

  // Filtrowanie eventów
  useEffect(() => {
    // Najpierw generuj wydarzenia wielodniowe dla użytkowników
    const expandedEvents = generateMultiDayEvents(events);
    
    let filtered = [...expandedEvents];

    const isSearching = searchQuery.trim().length > 0;

    // Wyszukiwanie artystów/zespołów
    if (isSearching) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event =>
        event.name.toLowerCase().includes(query) ||
        event.city.toLowerCase().includes(query) ||
        (event.description && event.description.toLowerCase().includes(query))
      );
    }

    if (filters.voivodeship) {
      filtered = filtered.filter(event =>
        getVoivodeshipForEvent(event) === filters.voivodeship
      );
    }

    if (filters.type) {
      filtered = filtered.filter(event => event.type === filters.type);
    }

    // Filtr daty tylko gdy nie szukamy po nazwie — przy wyszukiwaniu przeszukuj całą bazę
    if (filters.date && !isSearching) {
      filtered = filtered.filter(event => event.date === filters.date);
    }

    setFilteredEvents(filtered);
  }, [filters, events, searchQuery]);

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleToggleDarkMode = () => {
    setIsDarkMode(toggleTheme() === 'dark');
  };

  // Funkcja do obsługi dodawania wydarzenia
  const handleAddEvent = async (eventData: Event) => {
    try {
      const response = await apiService.submitEvent(eventData);
      alert('Dziękujemy za zgłoszenie! Wydarzenie zostanie sprawdzone i dodane do listy.');
      setShowEventForm(false);
      return response;
    } catch (error) {
      console.error('Error submitting event:', error);
      const message = error instanceof Error ? error.message : '';
      alert(message || 'Wystąpił błąd podczas wysyłania zgłoszenia. Spróbuj ponownie.');
      throw error;
    }
  };

  // Listy dla filtrów
  const eventTypes = [
    { value: 'Koncert', label: 'Koncert 🎵' },
    { value: 'Kultura', label: 'Kultura 🎭' },
    { value: 'Jedzenie', label: 'Jedzenie 🍽️' },
    { value: 'Festiwal', label: 'Festiwal 🎪' },
    { value: 'Bary', label: 'Bary 🍻' },
    { value: 'Sport', label: 'Sport ⚽' },
    { value: 'Nauka', label: 'Nauka 🔬' },
    { value: 'Kino', label: 'Kino 🎬' },
    { value: 'Targi', label: 'Targi 🏪' },
    { value: 'Kluby', label: 'Kluby 🎤' }
  ];

  const allVoivodeships: Voivodeship[] = VOIVODESHIPS;

  return (
    <div className="app">
      <button
        type="button"
        className={`theme-switch ${isDarkMode ? 'theme-switch--dark' : ''}`}
        onClick={handleToggleDarkMode}
        role="switch"
        aria-checked={isDarkMode}
        title={isDarkMode ? 'Tryb jasny' : 'Tryb ciemny'}
        aria-label={isDarkMode ? 'Włącz tryb jasny' : 'Włącz tryb ciemny'}
      >
        <span className="theme-switch-track" aria-hidden="true">
          <span className="theme-switch-icon theme-switch-icon--light">☀️</span>
          <span className="theme-switch-icon theme-switch-icon--dark">🌙</span>
          <span className="theme-switch-thumb" />
        </span>
      </button>

      {/* Header */}
      <header className="app-header">
        <h1>codzisiaj? 🎉</h1>
        <p>Znajdź wydarzenia w Twojej okolicy</p>
        
        <div className="header-links">
          {ready && dozynki && (
            <Link to="/dozynki" className="dozynki-nav-btn">
              <span className="dozynki-nav-icon" aria-hidden="true">🌾</span>
              <span>Dożynki</span>
            </Link>
          )}
          <Link to="/mapa" className="mapa-nav-btn" title="Mapa wydarzeń" aria-label="Mapa wydarzeń">
            <span className="mapa-nav-icon" aria-hidden="true">🗺️</span>
          </Link>
          {!isStandalone && (
            <>
              <InstallPrompt />
              <Link to="/about" className="about-link" title="Informacje" aria-label="Informacje">
                <span aria-hidden="true">ℹ️</span>
              </Link>
            </>
          )}
        </div>
        <NewsBanner />
      </header>

      {/* Panel wyszukiwania */}
      <div className="search-panel">
        <div className="search-content">
          <h3>Wyszukaj wydarzenia</h3>
          
          <div className="search-fields">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Szukaj w całej bazie — nazwa, miasto..."
              className="search-input"
            />
            
            <div className="filter-row">
              <select
                value={filters.voivodeship}
                onChange={(e) => handleFilterChange('voivodeship', e.target.value)}
                className="filter-select"
              >
                <option value="">Wszystkie województwa</option>
                {allVoivodeships.map((voivodeship) => (
                  <option key={voivodeship} value={voivodeship}>
                    {formatVoivodeshipLabel(voivodeship)}
                  </option>
                ))}
              </select>

              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="filter-select"
              >
                <option value="">Wszystkie typy</option>
                {eventTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <input
              type="date"
              value={filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
              className="date-input"
            />
          </div>
        </div>
      </div>

      {/* Lista wydarzeń */}
      <div className="events-container">
        <div className="events-header">
            <h2>
              {searchQuery.trim()
                ? `Wyniki wyszukiwania dla "${searchQuery}" — wszystkie daty (${filteredEvents.length})`
                : `Wydarzenia na ${filters.date} (${filteredEvents.length})`
              }
            </h2>
        </div>

        <div className="events-list">
          {isLoading ? (
            <div className="no-events">
              <p>Ładowanie wydarzeń... ⏳</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="no-events">
              <p>
                {searchQuery.trim() 
                  ? `Nie znaleziono wydarzeń dla "${searchQuery}" 😔`
                  : "Dzisiaj nic się nie dzieje 😴"
                }
              </p>
            </div>
          ) : (
            filteredEvents.map(event => (
              <div key={event.id} id={`event-${event.id}`} className="event-card" onClick={() => setSelectedEvent(event)} style={{cursor: 'pointer'}}>
                <div className="event-header">
                  <h3>{event.name}</h3>
                  <span className="event-type">
                    {getTypeEmoji(event.type)} {event.type}
                  </span>
                </div>
                <div className="event-details">
                  <p><strong>📅 Data:</strong> {event.date} {event.time && `• ${event.time}`}</p>
                  <p><strong>📍 Miasto:</strong> {event.city}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Przyciski akcji */}
      <div className="action-buttons">
        <button
          className="add-event-btn"
          onClick={() => setShowEventForm(true)}
          title="Dodaj wydarzenie"
        >
          ➕
        </button>
      </div>

      {/* Formularz dodawania wydarzenia */}
      {showEventForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EventForm
              onAddEvent={handleAddEvent}
              onUpdateEvent={() => {}}
              onClose={() => setShowEventForm(false)}
              editingEvent={null}
              isSimple={true}
            />
          </div>
        </div>
      )}

      {/* Podgląd wydarzenia */}
      {selectedEvent && (
        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="event-detail-header">
              <h2>{selectedEvent.name}</h2>
              <button onClick={() => setSelectedEvent(null)} className="close-btn">×</button>
            </div>
            <div className="event-detail-body">
              <p><strong>📅 Data:</strong> {selectedEvent.date} {selectedEvent.time && `• ${selectedEvent.time}`}</p>
              <p><strong>📍 Miasto:</strong> {selectedEvent.city}</p>
              <p><strong>🎯 Typ:</strong> {getTypeEmoji(selectedEvent.type)} {selectedEvent.type}</p>
              {selectedEvent.address && <p><strong>🏠 Adres:</strong> {selectedEvent.address}</p>}
              {selectedEvent.price && selectedEvent.showPrice && <p><strong>💰 Cena:</strong> {selectedEvent.price}</p>}
              {selectedEvent.organizer && <p><strong>👤 Organizator:</strong> {selectedEvent.organizer}</p>}
              {selectedEvent.phone && <p><strong>📞 Telefon:</strong> {selectedEvent.phone}</p>}
              {selectedEvent.email && <p><strong>📧 Email:</strong> {selectedEvent.email}</p>}
              {selectedEvent.link && <p><strong>🔗 Link:</strong> <a href={selectedEvent.link} target="_blank" rel="noopener noreferrer">Zobacz więcej</a></p>}
              {selectedEvent.description && <p><strong>📝 Opis:</strong> {selectedEvent.description}</p>}
            </div>
          </div>
        </div>
      )}

      {!isStandalone && (
        <footer className="app-footer">
          <nav className="app-footer-nav" aria-label="Informacje prawne">
            <Link to="/polityka-prywatnosci">Polityka prywatności</Link>
            <span className="app-footer-sep" aria-hidden="true">·</span>
            <Link to="/about">O aplikacji</Link>
          </nav>
        </footer>
      )}
    </div>
  );
}

export default App;